import React, { useState } from 'react';
import { ArrowDownRight, ArrowUpRight, Info, Maximize2, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import { formatCount, formatPercent } from '../../lib/formatNumber';
import type { ChartNeutralSet } from '../../lib/chartColors';

/**
 * Shared building blocks for the Analytics tabs.
 *
 * Extracted from AnalyticsDashboard.tsx when the Cloud panel (User Story 98)
 * moved into its own file: both files need the same KPI tile, section header,
 * empty state and grid metrics, and a panel importing them back from the
 * dashboard that renders it would be a circular import.
 */

/* ══════════════════════ number helpers ══════════════════════ */
export const num = (v: number | string | null | undefined) => Number(v ?? 0);
/** Two decimals — rendered via formatPercent(), also used for bar widths. */
export const pct = (a: number, b: number) =>
    b > 0 ? Math.round((a / b) * 10000) / 100 : 0;
/** Counts only — whole numbers with locale grouping, never two decimals. */
export const fmt = (n: number) => formatCount(n);

/* ══════════════════════ LAYOUT PRIMITIVES ══════════════════════
   Every tab shares the same two grid systems so spacing/proportions stay
   identical across tabs (see redesign notes in docs — the previous
   "col-2"/"col-3" classNames collided with Tailwind v4's own
   `.col-2 { grid-column: 2 }` utilities, silently shoving cards into explicit
   grid lines beyond the row's declared tracks and leaving a whole column
   permanently empty). KPI tiles use a uniform N-column grid; content sections
   (charts/tables) use a real 12-column grid with col-span-* utilities. */
export const KPI_GRID =
    'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 min-[1440px]:grid-cols-6 gap-4';
export const CONTENT_GRID = 'grid grid-cols-1 lg:grid-cols-12 gap-5 items-stretch';
export const COL_PRIMARY = 'lg:col-span-8 min-w-0';
export const COL_SECONDARY = 'lg:col-span-4 min-w-0';
export const COL_HALF = 'lg:col-span-6 min-w-0';

/* ══════════════════════ CHART DEFAULTS ══════════════════════ */
export const CHART_HEIGHT = 300;
// Explicit resize hooks (already apexcharts' default, restated for clarity) so
// charts redraw when their container's width changes — sidebar collapse,
// window resize, or a grid reflow — not just on an actual window "resize" event.
export const chartFont = {
    fontFamily: 'Inter, system-ui, sans-serif',
    redrawOnParentResize: true,
    redrawOnWindowResize: true,
};

/* ══════════════════════ Variance / delta bar builders (Story 109) ══════════════════════
   Two shapes for "actual vs a purchased/prior-period baseline" — a plain bar
   or a part-to-whole donut can't represent either case correctly (see the
   chart-taxonomy audit): a donut breaks once actual exceeds the baseline
   (over-allocated seats), and a grouped this/last bar makes the reader
   subtract two bar heights by eye to see which category actually moved.
   Both take a theme's neutrals/tooltip-theme from the caller rather than
   computing their own, since AnalyticsDashboard/CloudAnalyticsPanel and
   DashboardHome each re-derive theirs from `lib/chartColors.ts` per render. */

export interface VarianceBarRow {
    label: string;
    /** The value actually observed — e.g. seats assigned. */
    actual: number;
    /** The baseline it's measured against — e.g. seats purchased. */
    target: number;
}

/**
 * One "actual" bar per row, red when it exceeds `target` and indigo
 * otherwise, with `target` drawn as a dashed annotation line rather than a
 * second bar — a bar that crosses the line is over the baseline. Built for
 * single-row callers (today's only use is one aggregate row); ApexCharts'
 * yaxis annotations span the full plot width, so multiple rows with
 * different targets would draw overlapping lines rather than one per bar.
 */
export const varianceBarOptions = (
    id: string, rows: VarianceBarRow[], neutrals: ChartNeutralSet, tooltipTheme: 'light' | 'dark',
    actualLabel = 'Actual', targetLabel = 'Target',
): ApexCharts.ApexOptions => ({
    chart: { id, type: 'bar', ...chartFont, toolbar: { show: false } },
    colors: rows.map((r) => (r.actual > r.target ? '#e11d48' : '#4f46e5')),
    plotOptions: { bar: { borderRadius: 6, borderRadiusApplication: 'end', columnWidth: rows.length > 1 ? '55%' : '30%', distributed: true } },
    dataLabels: {
        enabled: true, offsetY: -22,
        style: { fontSize: '12px', fontWeight: 700, colors: [neutrals.labelStrong] },
        formatter: (v: number) => fmt(v),
    },
    legend: { show: false },
    xaxis: {
        categories: rows.map((r) => r.label),
        axisBorder: { show: false }, axisTicks: { show: false },
        labels: { style: { fontSize: '11px', fontWeight: '600', colors: neutrals.axisLabel } },
    },
    yaxis: { labels: { style: { fontSize: '11px', colors: neutrals.axisLabel } } },
    grid: { borderColor: neutrals.gridLine, strokeDashArray: 4, xaxis: { lines: { show: false } } },
    annotations: {
        yaxis: rows.map((r) => ({
            y: r.target,
            borderColor: neutrals.labelStrong,
            strokeDashArray: 4,
            label: {
                text: `${targetLabel}: ${fmt(r.target)}`,
                style: { fontSize: '10px', color: neutrals.labelStrong, background: 'transparent' },
                position: 'right',
            },
        })),
    },
    tooltip: {
        theme: tooltipTheme,
        y: {
            formatter: (v: number, ctx: any) => {
                const row = rows[ctx?.dataPointIndex ?? 0];
                return row ? `${actualLabel}: ${fmt(v)} · ${targetLabel}: ${fmt(row.target)}` : fmt(v);
            },
        },
    },
});

/**
 * One diverging bar per category — `current - prior` against a zero
 * baseline — green for an increase, red for a decrease. Replaces a grouped
 * two-series bar when the point of the chart is "what moved", not "what were
 * the two raw totals" (the tooltip still carries both raw numbers).
 */
export const deltaBarOptions = (
    id: string, categories: string[], current: number[], prior: number[],
    neutrals: ChartNeutralSet, tooltipTheme: 'light' | 'dark',
    currentLabel = 'This period', priorLabel = 'Last period',
): ApexCharts.ApexOptions => ({
    chart: { id, type: 'bar', ...chartFont, toolbar: { show: false } },
    colors: categories.map((_, i) => ((current[i] ?? 0) - (prior[i] ?? 0) >= 0 ? '#059669' : '#e11d48')),
    plotOptions: { bar: { borderRadius: 6, borderRadiusApplication: 'end', columnWidth: '50%', distributed: true } },
    dataLabels: {
        enabled: true,
        style: { fontSize: '11px', fontWeight: 700, colors: [neutrals.labelStrong] },
        formatter: (v: number) => (v > 0 ? `+${fmt(v)}` : fmt(v)),
    },
    legend: { show: false },
    xaxis: {
        categories,
        axisBorder: { show: false }, axisTicks: { show: false },
        labels: { style: { fontSize: '11px', fontWeight: '600', colors: neutrals.axisLabel } },
    },
    yaxis: { labels: { style: { fontSize: '11px', colors: neutrals.axisLabel } } },
    grid: { borderColor: neutrals.gridLine, strokeDashArray: 4, xaxis: { lines: { show: false } } },
    tooltip: {
        theme: tooltipTheme,
        y: {
            formatter: (v: number, ctx: any) => {
                const i = ctx?.dataPointIndex ?? 0;
                return `${v > 0 ? '+' : ''}${fmt(v)} (${currentLabel}: ${fmt(current[i] ?? 0)} · ${priorLabel}: ${fmt(prior[i] ?? 0)})`;
            },
        },
    },
});

/* ══════════════════════ KPI card ══════════════════════ */
export interface KpiCardProps {
    icon: React.ReactNode;
    label: string;
    value: string | number;
    sub?: string;
    trend?: { value: number; label: string };
    accent?: string;
    iconBg?: string;
    /** When set, the card opens the KPI drill-down modal for this id on click. */
    kpiId?: string;
    onClick?: (kpiId: string) => void;
}
export const KpiCard: React.FC<KpiCardProps> = ({ icon, label, value, sub, trend, accent = '#4f46e5', iconBg = 'rgba(79,70,229,0.10)', kpiId, onClick }) => {
    const trendUp = trend && trend.value >= 0;
    const clickable = !!(kpiId && onClick);
    return (
        <Card
            className={cn('h-full flex flex-col', clickable && 'cursor-pointer hover:border-primary/20 hover:shadow-sm transition-all')}
            onClick={clickable ? () => onClick!(kpiId!) : undefined}
        >
            <CardContent className="p-4 flex-1 flex flex-col justify-between">
                <div className="analytics-kpi-top">
                    <div className="analytics-kpi-icon" style={({ background: iconBg, color: accent }) as React.CSSProperties}>
                        {icon}
                    </div>
                    <p className="analytics-kpi-value text-foreground">{typeof value === 'number' ? fmt(value) : value}</p>
                    {trend && (
                        <span className={`analytics-trend ${trendUp ? 'trend-up' : 'trend-down'}`}>
                            {trendUp ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                            {formatPercent(Math.abs(trend.value))}
                        </span>
                    )}
                </div>
                <div>
                    <p className="analytics-kpi-label text-muted-foreground">{label}</p>
                    {sub && <p className="analytics-kpi-sub text-muted-foreground">{sub}</p>}
                </div>
            </CardContent>
        </Card>
    );
};

/* ══════════════════════ Section header ══════════════════════ */
/* The `analytics-section-*` class names carry no CSS anywhere in the project,
   so a title and its note used to render as one unbroken run of text
   ("Assets by AgeFrom purchase or rental-receipt date"). The note always
   sits on its own line below the title (never inline beside it) — on wide
   cards an inline long note used to wrap mid-sentence right after the title,
   reading as one run-on line; stacking is consistent regardless of card
   width or note length. The class names are kept because tests and E2E
   specs select on them. */
export const SectionHead: React.FC<{ title: string; note?: string }> = ({ title, note }) => (
    <div className="analytics-section-head flex flex-col gap-0.5">
        <span className="analytics-section-title text-foreground text-sm font-semibold">{title}</span>
        {note && <span className="analytics-section-note text-muted-foreground text-xs font-normal">{note}</span>}
    </div>
);

/* ══════════════════════ Progress bar row ══════════════════════ */
export const ProgressRow: React.FC<{ label: string; value: number; total: number; color: string }> = ({ label, value, total, color }) => {
    const p = pct(value, total);
    return (
        <div className="analytics-progress-row">
            <div className="analytics-progress-label">
                <span>{label}</span>
                <span className="analytics-progress-count">{fmt(value)} <small>({formatPercent(p)})</small></span>
            </div>
            <div className="analytics-progress-track">
                <div className="analytics-progress-fill" style={({ width: `${p}%`, background: color }) as React.CSSProperties} />
            </div>
        </div>
    );
};

/* ══════════════════════ Focus mode ══════════════════════
   A card that can be opened on its own, Power BI style: every visual carries an
   expand control in its header and re-renders at viewport size in a dialog.

   `children` is a render function rather than a node because the same visual is
   drawn at two sizes — a chart handed a fixed 300px height would open in focus
   mode at 300px, which defeats the point.

   While focused, the inline copy is replaced by a same-height placeholder. Two
   ApexCharts instances sharing one `chart.id` would both sit in the library's
   global registry, and the inline one is behind the overlay where nobody can
   read it anyway. */
export interface FocusableVisualProps {
    title: string;
    note?: string;
    /** Card wrapper classes — the grid col-span belongs here. */
    className?: string;
    /** Inline body height passed to `children`; focus mode computes its own. */
    height?: number;
    /**
     * Body sits in its own edge-to-edge CardContent, for visuals that fill the
     * card (tables). Default keeps header and body in one padded block.
     */
    flush?: boolean;
    children: (ctx: { height: number; focused: boolean }) => React.ReactNode;
}

/** Focus height tracks the viewport so the chart actually gains room. */
const focusHeight = () =>
    Math.max(360, Math.round((typeof window === 'undefined' ? 900 : window.innerHeight) * 0.6));

export const FocusableVisual: React.FC<FocusableVisualProps> = ({
    title, note, className, height = CHART_HEIGHT, flush, children,
}) => {
    const [focused, setFocused] = useState(false);

    const header = (
        <div className="flex items-start justify-between gap-2">
            <SectionHead title={title} note={note} />
            <button
                type="button"
                onClick={() => setFocused(true)}
                aria-label={`Expand ${title}`}
                title="Focus mode"
                className="-mt-0.5 shrink-0 rounded-md p-1.5 text-muted-foreground/70 transition-colors cursor-pointer hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
                <Maximize2 size={15} />
            </button>
        </div>
    );

    const placeholder = (
        <div
            className="flex items-center justify-center rounded-md border border-dashed border-border text-xs text-muted-foreground"
            style={{ height: flush ? 120 : height }}
        >
            Open in focus view
        </div>
    );

    return (
        <>
            <Card className={className}>
                {flush ? (
                    <>
                        <CardContent className="p-5 pb-0">{header}</CardContent>
                        <CardContent className={cn('p-0', focused && 'p-5')}>
                            {focused ? placeholder : children({ height, focused: false })}
                        </CardContent>
                    </>
                ) : (
                    <CardContent className="p-5">
                        {header}
                        {focused ? placeholder : children({ height, focused: false })}
                    </CardContent>
                )}
            </Card>

            <Dialog open={focused} onOpenChange={setFocused}>
                {/* max-w needs the sm: prefix — an unprefixed one loses to the
                    component's own sm:max-w-lg and clamps every focus view to 512px. */}
                <DialogContent className="sm:max-w-[min(80rem,calc(100vw-4rem))]">
                    <DialogHeader>
                        <DialogTitle>{title}</DialogTitle>
                        {note && <DialogDescription>{note}</DialogDescription>}
                    </DialogHeader>
                    <div className="min-w-0 overflow-x-auto">
                        {focused && children({ height: focusHeight(), focused: true })}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

/* ══════════════════════ Empty / unavailable state ══════════════════════
   Replaces bare "…unavailable" text floating in a full chart-height block.
   Always renders inside its own bordered Card so unavailable sections read as
   a deliberate state, not a layout gap, and never reserve more vertical space
   than their content needs. */
export interface EmptyStateCardProps {
    icon?: React.ReactNode;
    title: string;
    reason?: string;
    action?: { label: string; onClick: () => void };
    spinning?: boolean;
}
export const EmptyStateCard: React.FC<EmptyStateCardProps> = ({ icon, title, reason, action, spinning }) => (
    <div className="flex flex-col items-center justify-center gap-2 py-8 px-4 text-center">
        <div className="text-muted-foreground/60">
            {spinning ? <RefreshCw className="animate-spin" size={20} /> : (icon ?? <Info size={20} />)}
        </div>
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        {reason && <p className="text-xs text-muted-foreground/80 max-w-sm">{reason}</p>}
        {action && (
            <Button variant="outline" size="sm" onClick={action.onClick} className="mt-1">
                {action.label}
            </Button>
        )}
    </div>
);
